import { parseLocalDate } from './dates';

type EventTiming = {
  status: string;
  start_date: string;
  end_date?: string | null;
  registration_deadline?: string | null;
};

export type EventPhase = 'planned' | 'upcoming' | 'soon' | 'now' | 'finished';

export const EVENT_PHASE_LABELS: Record<EventPhase, string> = {
  planned: 'Planned',
  upcoming: 'Upcoming',
  soon: 'Happening Soon',
  now: 'Happening Now',
  finished: 'Finished',
};

const DAY_MS = 24 * 60 * 60 * 1000;
export const HAPPENING_SOON_DAYS = 14;
export const PAST_EVENT_GRACE_DAYS = 7;

function endOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

/** End of the later of start/end, so an end date entered before the start can't end the event early. */
function endOfLastDay(event: EventTiming): Date {
  const start = parseLocalDate(event.start_date);
  const end = event.end_date ? parseLocalDate(event.end_date) : start;
  return endOfDay(end > start ? end : start);
}

/** Archived events, and any event (regardless of status) whose last day has passed. */
export function hasEventEnded(event: EventTiming, now: Date = new Date()): boolean {
  return event.status === 'archived' || now.getTime() > endOfLastDay(event).getTime();
}

/**
 * Whether the event belongs on Past Events rather than Upcoming. Finished events
 * stay on Upcoming (labelled Finished) for a grace week unless archived.
 */
export function isPastEvent(event: EventTiming, now: Date = new Date()): boolean {
  if (event.status === 'archived') return true;
  return now.getTime() > endOfLastDay(event).getTime() + PAST_EVENT_GRACE_DAYS * DAY_MS;
}

export function getEventPhase(event: EventTiming, now: Date = new Date()): EventPhase {
  if (hasEventEnded(event, now)) return 'finished';
  if (event.status === 'draft') return 'planned';
  const start = parseLocalDate(event.start_date);
  if (now.getTime() >= start.getTime()) return 'now';
  if (start.getTime() - now.getTime() <= HAPPENING_SOON_DAYS * DAY_MS) return 'soon';
  return 'upcoming';
}

/**
 * Published, not yet ended, and on or before the registration deadline's
 * calendar day (local time, matching how the deadline is displayed).
 */
export function isRegistrationOpen(event: EventTiming, now: Date = new Date()): boolean {
  if (event.status !== 'published' || hasEventEnded(event, now)) return false;
  if (!event.registration_deadline) return true;
  return now.getTime() <= endOfDay(parseLocalDate(event.registration_deadline)).getTime();
}
